import CloseIcon from "@mui/icons-material/Close";
import {
    Box,
    Button,
    Checkbox,
    Divider,
    FormControlLabel,
    IconButton,
    Modal,
    TextField,
    Typography,
    useMediaQuery,
} from "@mui/material";
import CircularProgress from "@mui/material/CircularProgress";
import { useTheme } from "@mui/material/styles";
import axios from "axios";

const CallbackModal = ({
  open,
  onClose,
  selectedVendor,
  userData,
  setUserData,
  errors,
  setErrors,
  loading,
  setLoading,
  quoteAllProviders,
  setQuoteAllProviders,
  selectedVendors,
  setSelectedVendors,
  filteredVendorsByCategory,
  callbackRequests,
  setCallbackRequests,
  selectedCategory,
  storedUser,
  setConfirmationMessage,
  setConfirmationIsError,
  setConfirmationOpen,
  isMobile,
}) => {
  const theme = useTheme();
  const isTablet = useMediaQuery(theme.breakpoints.between("sm", "md"));

  const modalStyle = {
    position: "absolute",
    top: isMobile ? "0" : "50%",
    left: isMobile ? "0" : "50%",
    transform: isMobile ? "none" : "translate(-50%, -50%)",
    width: isMobile ? "100vw" : isTablet ? "80vw" : "min(360px, 85vw)",
    height: isMobile ? "100vh" : "auto",
    maxWidth: isMobile ? "100vw" : "500px",
    bgcolor: "background.paper",
    borderRadius: isMobile ? 0 : "8px",
    boxShadow: isMobile ? 0 : 24,
    p: { xs: 1.5, sm: 3 },
    maxHeight: isMobile ? "100vh" : "90vh",
    overflowY: "auto",
    WebkitOverflowScrolling: "touch",
    display: "flex",
    flexDirection: "column",
    "&::-webkit-scrollbar": {
      width: "8px",
    },
    "&::-webkit-scrollbar-thumb": {
      backgroundColor: "grey.400",
      borderRadius: "4px",
    },
    "&::-webkit-scrollbar-track": {
      backgroundColor: "grey.100",
    },
  };

  const validateFields = () => {
    const newErrors = {};
    if (!userData.firstName.trim()) {
      newErrors.firstName = "First Name is required.";
    }
    if (!userData.lastName.trim()) {
      newErrors.lastName = "Last Name is required.";
    }
    if (!userData.contactNumber.trim()) {
      newErrors.contactNumber = "Contact Number is required.";
    }
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleQuoteAllProvidersChange = (event) => {
    setQuoteAllProviders(event.target.checked);
    if (!event.target.checked) {
      setSelectedVendors([]);
    }
  };

  const handleSubmitQuote = () => {
    if (!validateFields()) return;

    setLoading(true);

    const quoteData = {
      ...userData,
      vendorId: selectedVendor?.id,
      quoteAllProviders,
      selectedVendors,
      category: selectedCategory ? selectedCategory.replace(/-/g, " ") : "",
      user: storedUser,
    };

    axios
      .post(`${process.env.REACT_APP_BACKEND_URL}/api/callback`, quoteData)
      .then((response) => {
        const allVendorsToProcess = [selectedVendor?.id, ...selectedVendors];
        allVendorsToProcess.forEach((vendorId) => {
          setCallbackRequests((prev) => ({
            ...prev,
            [vendorId]: { createdAt: new Date(), id: response.data.id },
          }));
        });

        setLoading(false);
        onClose();
        setConfirmationMessage(
          "Your request for a call-back has been submitted. A consultant will be contacting you soon."
        );
        setConfirmationIsError(false);
        setConfirmationOpen(true);
        window.history.pushState(null, "", window.location.href);
      })
      .catch((error) => {
        console.error("Error submitting quote:", error);
        setLoading(false);
        setConfirmationMessage(
          "An error occurred while submitting your request. Please try again."
        );
        setConfirmationIsError(true);
        setConfirmationOpen(true);
        window.history.pushState(null, "", window.location.href);
      });
  };

  return (
    <Modal open={open} onClose={onClose}>
      <Box sx={modalStyle}>
        {isMobile && (
          <IconButton
            onClick={onClose}
            sx={{
              position: "absolute",
              top: 8,
              right: 8,
              color: "grey.500",
              bgcolor: "grey.100",
              "&:hover": { bgcolor: "grey.200" },
              borderRadius: "50%",
              p: 0.8,
            }}
            aria-label="Close modal"
          >
            <CloseIcon sx={{ fontSize: { xs: "1.2rem", sm: "1.4rem" } }} />
          </IconButton>
        )}
        <Typography
          variant="h6"
          sx={{
            fontSize: { xs: "1.1rem", sm: "1.2rem" },
            fontWeight: 600,
            mb: 0.5,
            pr: isMobile ? 4 : 0,
          }}
        >
          Request a Callback from {selectedVendor?.name}
        </Typography>
        <Typography
          variant="body2"
          color="text.secondary"
          sx={{
            mb: 1.5,
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
            lineHeight: 1.4,
          }}
        >
          Leave your contact details below, and a{" "}
          <strong>{selectedVendor?.name}</strong> representative will contact
          you shortly.
        </Typography>

        <Divider sx={{ mb: 1.5, borderColor: "grey.200" }} />

        <TextField
          label="First Name"
          value={userData.firstName}
          onChange={(e) =>
            setUserData({ ...userData, firstName: e.target.value })
          }
          fullWidth
          margin="dense"
          required
          error={!!errors.firstName}
          helperText={errors.firstName}
          sx={{
            mb: 1,
            "& .MuiInputBase-input": { fontSize: { xs: "0.75rem", sm: "0.8rem" } },
            "& .MuiFormLabel-root": { fontSize: { xs: "0.7rem", sm: "0.75rem" } },
            "& .MuiInputBase-root": { borderRadius: "6px" },
          }}
        />
        <TextField
          label="Last Name"
          value={userData.lastName}
          onChange={(e) =>
            setUserData({ ...userData, lastName: e.target.value })
          }
          fullWidth
          margin="dense"
          required
          error={!!errors.lastName}
          helperText={errors.lastName}
          sx={{
            mb: 1,
            "& .MuiInputBase-input": { fontSize: { xs: "0.75rem", sm: "0.8rem" } },
            "& .MuiFormLabel-root": { fontSize: { xs: "0.7rem", sm: "0.75rem" } },
            "& .MuiInputBase-root": { borderRadius: "6px" },
          }}
        />
        <TextField
          label="Contact Number"
          value={userData.contactNumber}
          onChange={(e) =>
            setUserData({ ...userData, contactNumber: e.target.value })
          }
          fullWidth
          margin="dense"
          required
          error={!!errors.contactNumber}
          helperText={errors.contactNumber}
          sx={{
            mb: 1,
            "& .MuiInputBase-input": { fontSize: { xs: "0.75rem", sm: "0.8rem" } },
            "& .MuiFormLabel-root": { fontSize: { xs: "0.7rem", sm: "0.75rem" } },
            "& .MuiInputBase-root": { borderRadius: "6px" },
          }}
        />
        <TextField
          label="Email"
          value={userData.email}
          onChange={(e) =>
            setUserData({ ...userData, email: e.target.value })
          }
          fullWidth
          margin="dense"
          sx={{
            mb: 1.5,
            "& .MuiInputBase-input": { fontSize: { xs: "0.75rem", sm: "0.8rem" } },
            "& .MuiFormLabel-root": { fontSize: { xs: "0.7rem", sm: "0.75rem" } },
            "& .MuiInputBase-root": { borderRadius: "6px" },
          }}
        />

        {filteredVendorsByCategory.length > 1 && (
          <FormControlLabel
            sx={{
              border: "1px solid",
              borderColor: "grey.200",
              borderRadius: "6px",
              p: { xs: 1, sm: 1.2 },
              mb: 1.5,
              bgcolor: "grey.50",
            }}
            control={
              <Checkbox
                checked={quoteAllProviders}
                onChange={handleQuoteAllProvidersChange}
                color="primary"
                sx={{
                  "& .MuiSvgIcon-root": { fontSize: { xs: "1rem", sm: "1.1rem" } },
                }}
              />
            }
            label={
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
              >
                Also receive quotes from other{" "}
                <strong>
                  {selectedCategory ? selectedCategory.replace(/-/g, " ") : ""}
                </strong>{" "}
                providers.
              </Typography>
            }
          />
        )}

        {quoteAllProviders && (
          <Box sx={{ mb: 1.5 }}>
            <Typography
              variant="body2"
              sx={{
                mb: 1,
                fontSize: { xs: "0.75rem", sm: "0.8rem" },
                fontWeight: 500,
              }}
            >
              Select providers:
            </Typography>
            {filteredVendorsByCategory.length > 0 ? (
              filteredVendorsByCategory
                .filter(
                  (vendor) =>
                    vendor.id !== selectedVendor?.id &&
                    !callbackRequests[vendor.id]
                )
                .map((vendor) => (
                  <FormControlLabel
                    key={vendor.id}
                    sx={{
                      width: "100%",
                      py: 0.3,
                      "& .MuiTypography-root": {
                        fontSize: { xs: "0.75rem", sm: "0.8rem" },
                      },
                    }}
                    control={
                      <Checkbox
                        checked={selectedVendors.includes(vendor.id)}
                        onChange={() =>
                          setSelectedVendors((prev) =>
                            prev.includes(vendor.id)
                              ? prev.filter((id) => id !== vendor.id)
                              : [...prev, vendor.id]
                          )
                        }
                        sx={{
                          "& .MuiSvgIcon-root": {
                            fontSize: { xs: "1rem", sm: "1.1rem" },
                          },
                        }}
                      />
                    }
                    label={vendor.name}
                  />
                ))
            ) : (
              <Typography
                variant="body2"
                color="text.secondary"
                sx={{ fontSize: { xs: "0.75rem", sm: "0.8rem" } }}
              >
                No additional providers available.
              </Typography>
            )}
          </Box>
        )}

        <Button
          variant="contained"
          color="primary"
          onClick={handleSubmitQuote}
          fullWidth
          sx={{
            py: { xs: 0.8, sm: 1 },
            fontSize: { xs: "0.75rem", sm: "0.8rem" },
            borderRadius: "6px",
            textTransform: "none",
            fontWeight: 500,
            color: "#fff",
            boxShadow: "none",
          }}
          disabled={loading}
        >
          {loading ? (
            <CircularProgress size={isMobile ? 20 : 24} color="inherit" />
          ) : (
            "Submit"
          )}
        </Button>
      </Box>
    </Modal>
  );
};

export default CallbackModal;